/**
 * Google Maps Geocoding Service
 *
 * This service provides geocoding functionality using the Google Maps JavaScript API
 * instead of react-geocode, allowing for better API key restrictions and security.
 */

export interface GeocodeResult {
  results: Array<{
    geometry: {
      location: {
        lat: number
        lng: number
      }
    }
    formatted_address: string
  }>
}

export interface GeocodeError {
  status: string
  error_message?: string
}

class GoogleMapsService {
  private geocoder: any = null
  private initializationPromise: Promise<any> | null = null

  /**
   * Initialize the geocoder instance
   * This method ensures the Google Maps API is loaded before creating the geocoder
   */
  private async initializeGeocoder(): Promise<any> {
    if (this.geocoder) {
      return this.geocoder
    }

    // Return existing initialization promise if already in progress
    if (this.initializationPromise) {
      return this.initializationPromise
    }

    this.initializationPromise = this.performInitialization()
    return this.initializationPromise
  }

  private async performInitialization(): Promise<any> {
    // Wait for Google Maps API to be loaded with retry mechanism
    const maxRetries = 30 // 30 seconds total
    let retries = 0

    while (retries < maxRetries) {
      if (window.google?.maps?.Geocoder) {
        try {
          this.geocoder = new window.google.maps.Geocoder()
          console.log('Google Maps Geocoder initialized successfully')
          return this.geocoder
        } catch (error) {
          console.error('Error initializing Google Maps Geocoder:', error)
          throw new Error(`Failed to initialize Google Maps Geocoder: ${error}`)
        }
      }

      // Wait 1 second before retrying
      await new Promise((resolve) => setTimeout(resolve, 1000))
      retries++
    }

    throw new Error(
      'Google Maps API not loaded after 30 seconds. Please check:\n' +
        '1. The script tag is included in your HTML\n' +
        '2. Your API key is valid and has proper restrictions\n' +
        '3. The required APIs are enabled in Google Cloud Console\n' +
        '4. Billing is enabled for your Google Cloud project'
    )
  }

  /**
   * Geocode an address to get latitude and longitude coordinates
   *
   * @param address - The address string to geocode
   * @returns Promise that resolves with geocoding results
   */
  async fromAddress(address: string): Promise<GeocodeResult> {
    try {
      const geocoder = await this.initializeGeocoder()

      return new Promise((resolve, reject) => {
        console.log(`Attempting to geocode address: "${address}"; ${geocoder}`)

        geocoder.geocode({ address }, (results: any, status: any) => {
          console.log(`Geocoding status: ${status}`, results)

          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            // Transform the results to match the react-geocode format
            const transformedResults = results.map((result: any) => ({
              geometry: {
                location: {
                  lat: result.geometry.location.lat(),
                  lng: result.geometry.location.lng()
                }
              },
              formatted_address: result.formatted_address
            }))

            console.log('Geocoding successful:', transformedResults[0])
            resolve({
              results: transformedResults
            })
          } else {
            const errorDetails = this.getDetailedErrorMessage(status)
            console.error('Geocoding failed:', errorDetails)

            const error: GeocodeError = {
              status: status,
              error_message: errorDetails
            }
            reject(error)
          }
        })
      })
    } catch (error) {
      console.error('Error in fromAddress:', error)
      throw error
    }
  }

  /**
   * Get detailed error message for geocoding status
   */
  private getDetailedErrorMessage(status: string): string {
    const errorMessages: { [key: string]: string } = {
      ERROR:
        'A geocoding request could not be processed due to a server error.',
      INVALID_REQUEST: 'The geocode request was invalid.',
      OVER_QUERY_LIMIT:
        'You have exceeded your daily quota or per-second rate limit.',
      REQUEST_DENIED:
        'The geocode request was denied. Check your API key restrictions.',
      UNKNOWN_ERROR:
        'A geocoding request could not be processed due to an unknown error.',
      ZERO_RESULTS: 'No results were found for the provided address.'
    }

    const baseMessage =
      errorMessages[status] || `Geocoding failed with status: ${status}`

    if (status === 'REQUEST_DENIED') {
      return `${baseMessage}\n\nPlease check:\n- API key is valid\n- Geocoding API is enabled\n- HTTP referrer restrictions allow your domain\n- Billing is enabled`
    }

    return baseMessage
  }

  /**
   * Reverse geocode coordinates to get address information
   *
   * @param lat - Latitude
   * @param lng - Longitude
   * @returns Promise that resolves with reverse geocoding results
   */
  async fromLatLng(lat: number, lng: number): Promise<GeocodeResult> {
    try {
      const geocoder = await this.initializeGeocoder()

      return new Promise((resolve, reject) => {
        const latLng = new google.maps.LatLng(lat, lng)

        geocoder.geocode({ location: latLng }, (results: any, status: any) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            // Transform the results to match the react-geocode format
            const transformedResults = results.map((result: any) => ({
              geometry: {
                location: {
                  lat: result.geometry.location.lat(),
                  lng: result.geometry.location.lng()
                }
              },
              formatted_address: result.formatted_address
            }))

            resolve({
              results: transformedResults
            })
          } else {
            const error: GeocodeError = {
              status: status,
              error_message: `Reverse geocoding failed: ${status}`
            }
            reject(error)
          }
        })
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if Google Maps API is loaded and ready
   */
  isApiLoaded(): boolean {
    return !!window.google?.maps?.Geocoder
  }

  /**
   * Diagnostic method to help troubleshoot API setup issues
   */
  diagnoseApiSetup(): void {
    console.log('=== Google Maps API Diagnostic ===')
    console.log('window.google exists:', !!window.google)
    console.log('window.google.maps exists:', !!window.google?.maps)
    console.log(
      'window.google.maps.Geocoder exists:',
      !!window.google?.maps?.Geocoder
    )
    console.log(
      'window.google.maps.GeocoderStatus exists:',
      !!window.google?.maps?.GeocoderStatus
    )
    console.log('Current URL:', window.location.href)
    console.log('Current domain:', window.location.hostname)

    const scripts = Array.from(
      document.querySelectorAll('script[src*="maps.googleapis.com"]')
    )
    console.log('Google Maps script tags found:', scripts.length)
    scripts.forEach((script, index) => {
      console.log(`Script ${index + 1}:`, (script as HTMLScriptElement).src)
    })

    if (window.google?.maps) {
      console.log('Available Google Maps services:')
      console.log('- Geocoder:', !!window.google.maps.Geocoder)
      console.log('- Map:', !!window.google.maps.Map)
      console.log('- LatLng:', !!window.google.maps.LatLng)
      console.log('- places:', !!window.google.maps.places)
    }
    console.log('=== End Diagnostic ===')
  }
}

// Export a singleton instance
export const googleMapsService = new GoogleMapsService()
